import { useEffect, useState } from "react";
import {
	useCleanupNow,
	useCompactDb,
	useRetention,
	useSetRetention,
} from "../../hooks/queries";
import { Button } from "../ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

export function DataRetentionCard() {
	const { data, isLoading } = useRetention();
	const setRetention = useSetRetention();
	const cleanupNow = useCleanupNow();
	const compactDb = useCompactDb();
	const [payloadDays, setPayloadDays] = useState<number>(
		data?.payloadDays ?? 7,
	);
	const [requestDays, setRequestDays] = useState<number>(
		data?.requestDays ?? 365,
	);

	useEffect(() => {
		if (typeof data?.payloadDays === "number") setPayloadDays(data.payloadDays);
		if (typeof data?.requestDays === "number") setRequestDays(data.requestDays);
	}, [data?.payloadDays, data?.requestDays]);

	const disabled = isLoading || setRetention.isPending;
	const validPayload =
		Number.isFinite(payloadDays) && payloadDays >= 1 && payloadDays <= 365;
	const validRequests =
		Number.isFinite(requestDays) && requestDays >= 1 && requestDays <= 3650;

	return (
		<Card>
			<CardHeader>
				<CardTitle>Payload Retention</CardTitle>
				<CardDescription>
					Automatically delete request/response payloads older than this window.
					Analytics remain intact.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				<div className="flex items-center gap-2">
					<Label htmlFor="capture-payloads" className="w-28">
						Capture
					</Label>
					<Switch
						id="capture-payloads"
						checked={data?.capturePayloads ?? true}
						onCheckedChange={(capturePayloads) =>
							setRetention.mutate({ capturePayloads })
						}
						disabled={disabled}
					/>
					<span className="text-sm text-muted-foreground">
						{(data?.capturePayloads ?? true)
							? "Request/response bodies are stored"
							: "Off: only request metadata (tokens, cost, timings) is stored"}
					</span>
				</div>

				<div className="flex items-center gap-2">
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium w-28">Payloads</span>
						<Input
							type="number"
							min={1}
							max={365}
							value={payloadDays}
							onChange={(e) =>
								setPayloadDays(parseInt(e.target.value || "0", 10))
							}
							className="w-24"
						/>
						<span className="text-sm text-muted-foreground">days</span>
					</div>
					<Button
						size="sm"
						disabled={disabled || !validPayload}
						onClick={() => setRetention.mutate({ payloadDays })}
					>
						Save
					</Button>
				</div>

				<div className="flex items-center gap-2">
					{[7, 14, 30, 90].map((d) => (
						<Button
							key={d}
							variant="outline"
							size="sm"
							onClick={() => setPayloadDays(d)}
						>
							{d}d
						</Button>
					))}
				</div>

				<div className="flex items-center gap-2 pt-2">
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium w-28">Requests</span>
						<Input
							type="number"
							min={1}
							max={3650}
							value={requestDays}
							onChange={(e) =>
								setRequestDays(parseInt(e.target.value || "0", 10))
							}
							className="w-24"
						/>
						<span className="text-sm text-muted-foreground">days</span>
					</div>
					<Button
						size="sm"
						disabled={disabled || !validRequests}
						onClick={() => setRetention.mutate({ requestDays })}
					>
						Save
					</Button>
				</div>

				<div className="pt-2">
					<Button
						variant="secondary"
						size="sm"
						onClick={() => cleanupNow.mutate()}
						disabled={cleanupNow.isPending}
					>
						Clean up now
					</Button>
					<Button
						variant="outline"
						size="sm"
						className="ml-2"
						onClick={() => compactDb.mutate()}
						disabled={compactDb.isPending}
					>
						Compact database
					</Button>
				</div>

				{cleanupNow.data && (
					<p className="text-xs text-muted-foreground">
						Removed {cleanupNow.data.removedRequests} requests and{" "}
						{cleanupNow.data.removedPayloads} payloads older than{" "}
						{new Date(cleanupNow.data.cutoffIso).toLocaleString()}.
					</p>
				)}

				{compactDb.isSuccess && (
					<p className="text-xs text-muted-foreground">
						Database compacted. File size should reduce on disk.
					</p>
				)}
			</CardContent>
		</Card>
	);
}
